import { Box, Flex, Heading, Text, Spinner, Icon, VStack } from "@chakra-ui/react";
import { motion, useReducedMotion } from "framer-motion";
import { FaInbox, FaExclamationTriangle } from "react-icons/fa";
import { glassSurfaces } from "@/theme";
import { glassContentSx } from "@/theme/glassSx";

const MotionBox = motion(Box);

/**
 * White translucent liquid-glass variants with dark ink typography.
 * Anything textual must be rendered inside one of these surfaces.
 */
const VARIANT_STYLES = {
  subtle: {
    bg: glassSurfaces.subtle,
    borderColor: "rgba(255, 255, 255, 0.55)",
  },
  default: {
    bg: glassSurfaces.default,
    borderColor: "rgba(255, 255, 255, 0.65)",
  },
  strong: {
    bg: glassSurfaces.strong,
    borderColor: "rgba(255, 255, 255, 0.75)",
  },
  solid: {
    bg: glassSurfaces.solid,
    borderColor: "rgba(255, 255, 255, 0.85)",
  },
  accent: {
    bg: "linear-gradient(135deg, rgba(255,255,255,0.82), rgba(255,255,255,0.52))",
    borderColor: "rgba(255, 255, 255, 0.8)",
  },
};

export function GlassSurface({
  variant = "default",
  interactive = false,
  animate = false,
  children,
  sx,
  ...props
}) {
  const reduceMotion = useReducedMotion();
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.default;

  const content = (
    <Box
      bg={styles.bg}
      color="text.onGlass"
      border="1px solid"
      borderColor={styles.borderColor}
      borderRadius="surface"
      backdropFilter="blur(20px) saturate(150%)"
      boxShadow="glass"
      transition="box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease"
      _hover={
        interactive
          ? {
              boxShadow: "glassHover",
              borderColor: "rgba(255,255,255,0.95)",
              transform: reduceMotion ? undefined : "translateY(-2px)",
            }
          : undefined
      }
      sx={{
        ...glassContentSx,
        "@supports not ((-webkit-backdrop-filter: blur(1px)) or (backdrop-filter: blur(1px)))":
          {
            bg: "rgba(255, 255, 255, 0.94)",
          },
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );

  if (!animate || reduceMotion) return content;

  return (
    <MotionBox
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      w={props.w}
      h={props.h}
    >
      {content}
    </MotionBox>
  );
}

/**
 * Page titles live inside a glass bar: dark ink would be unreadable
 * directly over the photo background.
 */
export function PageHeader({ title, subtitle, actions, badge, ...props }) {
  return (
    <GlassSurface
      variant="strong"
      px={{ base: 4, md: 6 }}
      py={{ base: 4, md: 5 }}
      mb={{ base: 5, md: 8 }}
      {...props}
    >
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        gap={4}
      >
        <Box>
          {badge}
          <Heading
            as="h1"
            size={{ base: "lg", md: "xl" }}
            letterSpacing="-0.02em"
            mb={subtitle ? 1 : 0}
            color="text.onGlass"
          >
            {title}
          </Heading>
          {subtitle && (
            <Text color="text.onGlassMuted" fontSize={{ base: "sm", md: "md" }} maxW="720px">
              {subtitle}
            </Text>
          )}
        </Box>
        {actions && (
          <Flex gap={3} flexWrap="wrap" justify={{ base: "stretch", md: "flex-end" }}>
            {actions}
          </Flex>
        )}
      </Flex>
    </GlassSurface>
  );
}

export function MetricCard({
  label,
  value,
  hint,
  icon,
  accent = "brand.600",
  isLoading = false,
  ...props
}) {
  return (
    <GlassSurface variant="default" p={{ base: 4, md: 5 }} animate {...props}>
      <Flex justify="space-between" align="start" gap={3}>
        <Box>
          <Text fontSize="sm" color="text.onGlassMuted" fontWeight="medium">
            {label}
          </Text>
          {isLoading ? (
            <Spinner size="sm" mt={3} color="brand.600" />
          ) : (
            <Heading size="lg" mt={1} color="text.onGlass">
              {value}
            </Heading>
          )}
          {hint && (
            <Text fontSize="xs" color="text.onGlassSubtle" mt={2}>
              {hint}
            </Text>
          )}
        </Box>
        {icon && (
          <Flex
            align="center"
            justify="center"
            w="44px"
            h="44px"
            borderRadius="full"
            bg="whiteAlpha.800"
            color={accent}
            flexShrink={0}
          >
            <Icon as={icon} boxSize={5} />
          </Flex>
        )}
      </Flex>
    </GlassSurface>
  );
}

export function EmptyState({
  title = "Nada por aquí todavía",
  description,
  icon = FaInbox,
  action,
  ...props
}) {
  return (
    <GlassSurface variant="default" p={{ base: 8, md: 10 }} textAlign="center" {...props}>
      <VStack spacing={4}>
        <Flex
          w="64px"
          h="64px"
          borderRadius="full"
          align="center"
          justify="center"
          bg="whiteAlpha.800"
        >
          <Icon as={icon} boxSize={7} color="gray.500" />
        </Flex>
        <Heading size="md" color="text.onGlass">
          {title}
        </Heading>
        {description && (
          <Text color="text.onGlassMuted" maxW="420px">
            {description}
          </Text>
        )}
        {action}
      </VStack>
    </GlassSurface>
  );
}

export function ErrorState({
  title = "Algo salió mal",
  description = "No pudimos cargar esta información. Intenta de nuevo.",
  action,
  ...props
}) {
  return (
    <EmptyState
      title={title}
      description={description}
      icon={FaExclamationTriangle}
      action={action}
      {...props}
    />
  );
}

export function LoadingState({ label = "Cargando…", minH = "40vh", ...props }) {
  return (
    <Flex direction="column" align="center" justify="center" minH={minH} {...props}>
      <GlassSurface
        variant="strong"
        px={8}
        py={6}
        borderRadius="surface"
        textAlign="center"
      >
        <VStack spacing={3}>
          <Spinner size="xl" thickness="3px" color="brand.600" speed="0.75s" />
          <Text fontWeight="medium" color="text.onGlassMuted">
            {label}
          </Text>
        </VStack>
      </GlassSurface>
    </Flex>
  );
}

export default GlassSurface;
